import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, MapPinIcon, UsersIcon, CheckCircle2Icon, SparklesIcon, TrendingUpIcon, TargetIcon, PackageIcon, RotateCcwIcon, BarChart3Icon, FileTextIcon, ClipboardCheckIcon, MenuIcon, XIcon, XCircleIcon, TrophyIcon, DollarSignIcon, CalendarCheckIcon, LayoutDashboardIcon, HandshakeIcon, GlobeIcon, ShieldCheckIcon, HeadphonesIcon, PaletteIcon, LayersIcon, GraduationCapIcon, UsersRoundIcon, HeartIcon, QuoteIcon } from 'lucide-react';
import { Logo, Button, Section, FadeIn } from '../ui';
import { SiteFooter } from '../SiteFooter';
export function ForDMCsPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);
  const nav = [{
    label: 'For DMCs',
    href: '#hero'
  }, {
    label: 'How It Works',
    href: '#solution'
  }, {
    label: 'Proposals',
    href: '#proposal'
  }, {
    label: 'Reports',
    href: '#reports'
  }, {
    label: 'Partner Plans',
    href: '#partner'
  }];
  return <div className="min-h-screen bg-white font-sans text-slate-800" data-id="element-2702">
      {/* Header */}
      <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/85 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-5'}`} data-id="element-2703">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between" data-id="element-2704">
          <Link to="/" data-id="element-2705">
            <Logo data-id="element-2706" />
          </Link>
          <nav className="hidden lg:flex items-center gap-8" data-id="element-2707">
            <ul className="flex items-center gap-7" data-id="element-2708">
              {nav.map(n => <li key={n.label} data-id="element-2709">
                  <a href={n.href} className="text-sm font-medium text-slate-600 hover:text-brand-navy transition-colors" data-id="element-2710">
                    {n.label}
                  </a>
                </li>)}
            </ul>
            <Button variant="primary" data-id="element-2711">Book DMC Demo</Button>
          </nav>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2 text-slate-600" aria-label="Menu" data-id="element-2712">
            {mobileOpen ? <XIcon className="w-6 h-6" data-id="element-2713" /> : <MenuIcon className="w-6 h-6" data-id="element-2714" />}
          </button>
        </div>
        {mobileOpen && <div className="lg:hidden bg-white border-t border-slate-100 px-6 py-4 flex flex-col gap-3 shadow-lg" data-id="element-2715">
            {nav.map(n => <a key={n.label} href={n.href} onClick={() => setMobileOpen(false)} className="text-base font-medium text-slate-700 py-2" data-id="element-2716">
                {n.label}
              </a>)}
            <Button variant="primary" className="w-full mt-2" data-id="element-2717">
              Book DMC Demo
            </Button>
          </div>}
      </header>

      {/* Hero */}
      <section id="hero" className="relative pt-32 pb-20 md:pt-44 md:pb-28 overflow-hidden bg-white" data-id="element-2718">
        {/* Animated grid pattern */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.4]" style={{
        backgroundImage: 'linear-gradient(to right, rgb(15 23 42 / 0.05) 1px, transparent 1px), linear-gradient(to bottom, rgb(15 23 42 / 0.05) 1px, transparent 1px)',
        backgroundSize: '56px 56px',
        maskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)',
        WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)'
      }} data-id="element-2719" />

        <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-2720">
          <motion.div animate={{
          x: [0, 30, 0],
          y: [0, -20, 0]
        }} transition={{
          duration: 12,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-brand-teal/[0.08] rounded-full blur-3xl" data-id="element-2721" />
          <motion.div animate={{
          x: [0, -25, 0],
          y: [0, 30, 0]
        }} transition={{
          duration: 14,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute top-1/3 -right-40 w-[500px] h-[500px] bg-brand-coral/[0.06] rounded-full blur-3xl" data-id="element-2722" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-2723">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center" data-id="element-2724">
            <div className="lg:col-span-6" data-id="element-2725">
              <FadeIn data-id="element-2726">
                <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-1 pr-4 py-1 mb-8 shadow-sm relative overflow-hidden" data-id="element-2727">
                  <span className="bg-brand-coral/10 text-brand-coral text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider relative z-10" data-id="element-2728">
                    For DMCs
                  </span>
                  <span className="text-sm font-medium text-slate-600 relative z-10" data-id="element-2729">
                    A new layer for destination management
                  </span>
                  <motion.span aria-hidden initial={{
                  x: '-120%'
                }} animate={{
                  x: '320%'
                }} transition={{
                  duration: 2.4,
                  repeat: Infinity,
                  repeatDelay: 3,
                  ease: 'easeInOut'
                }} className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/80 to-transparent skew-x-12 pointer-events-none" data-id="element-2730" />
                </div>
              </FadeIn>

              <FadeIn delay={0.05} data-id="element-2731">
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight text-balance mb-6 text-brand-navy" data-id="element-2732">
                  Move beyond logistics. Sell the{' '}
                  <span className="relative inline-block" data-id="element-2733">
                    <span className="relative z-10 text-brand-teal" data-id="element-2734">
                      experience layer
                    </span>
                    <motion.span initial={{
                    scaleX: 0
                  }} animate={{
                    scaleX: 1
                  }} transition={{
                    duration: 0.9,
                    delay: 0.6,
                    ease: [0.65, 0, 0.35, 1]
                  }} style={{
                    transformOrigin: 'left'
                  }} className="absolute bottom-1 left-0 right-0 h-3 bg-brand-teal/20 -z-0" data-id="element-2735" />
                  </span>
                  .
                </h1>
              </FadeIn>

              <FadeIn delay={0.15} data-id="element-2736">
                <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-10 max-w-xl" data-id="element-2737">
                  Offsite+ helps DMCs turn corporate retreats, incentive trips,
                  and group events into structured, engaging, measurable
                  experiences — with AI-designed agendas, attendee intake, event
                  hubs, group matching, wellness preferences, and client-ready
                  reports.
                </p>
              </FadeIn>

              <FadeIn delay={0.25} data-id="element-2738">
                <div className="flex flex-col sm:flex-row gap-4 mb-10" data-id="element-2739">
                  <Button variant="primary" size="lg" className="group" data-id="element-2740">
                    Book DMC Demo
                    <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2741" />
                  </Button>
                  <Button variant="outline" size="lg" href="/designer" data-id="element-2742">
                    Design a Free Offsite
                  </Button>
                </div>
              </FadeIn>

              <FadeIn delay={0.35} data-id="element-2743">
                <div className="border-t border-slate-100 pt-8 grid grid-cols-3 gap-6 max-w-md" data-id="element-2744">
                  {[{
                  v: '+34%',
                  l: 'win rate uplift'
                }, {
                  v: '8.8/10',
                  l: 'avg. experience score'
                }, {
                  v: '<1 wk',
                  l: 'onboarding'
                }].map((s, i) => <div key={i} data-id="element-2745">
                      <div className="text-2xl font-bold text-brand-navy" data-id="element-2746">
                        {s.v}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 uppercase tracking-wider font-bold" data-id="element-2747">
                        {s.l}
                      </div>
                    </div>)}
                </div>
              </FadeIn>
            </div>

            {/* DMC Dashboard Mockup */}
            <FadeIn delay={0.4} className="lg:col-span-6 relative lg:h-[620px] flex items-center justify-center" data-id="element-2748">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-teal/10 to-brand-coral/10 rounded-full blur-3xl" data-id="element-2749" />

              {/* Decorative orbital ring */}
              <motion.div aria-hidden animate={{
              rotate: 360
            }} transition={{
              duration: 40,
              repeat: Infinity,
              ease: 'linear'
            }} className="hidden lg:block absolute w-[520px] h-[520px] rounded-full border border-dashed border-slate-200/80" data-id="element-2750">
                <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-brand-coral shadow-[0_0_0_4px_rgba(255,107,74,0.15)]" data-id="element-2751" />
                <span className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-2 h-2 rounded-full bg-brand-teal" data-id="element-2752" />
              </motion.div>

              {/* Floating accent — top left */}
              <motion.div initial={{
              opacity: 0,
              y: 20,
              x: -10
            }} animate={{
              opacity: 1,
              y: 0,
              x: 0
            }} transition={{
              delay: 1.1,
              duration: 0.6
            }} className="hidden md:block absolute top-8 -left-2 lg:left-4 z-20" data-id="element-2753">
                <motion.div animate={{
                y: [0, -8, 0]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="bg-white rounded-xl shadow-xl shadow-slate-300/40 border border-slate-100 px-3.5 py-2.5 flex items-center gap-2.5" data-id="element-2754">
                  <div className="bg-emerald-50 p-1.5 rounded-md" data-id="element-2755">
                    <TrophyIcon className="w-4 h-4 text-emerald-600" data-id="element-2756" />
                  </div>
                  <div data-id="element-2757">
                    <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-2758">
                      Proposal Won
                    </div>
                    <div className="text-sm font-bold text-brand-navy" data-id="element-2759">
                      $148k Value
                    </div>
                  </div>
                </motion.div>
              </motion.div>

              {/* Floating accent — bottom right */}
              <motion.div initial={{
              opacity: 0,
              y: 20,
              x: 10
            }} animate={{
              opacity: 1,
              y: 0,
              x: 0
            }} transition={{
              delay: 1.3,
              duration: 0.6
            }} className="hidden md:block absolute bottom-10 -right-2 lg:right-4 z-20" data-id="element-2760">
                <motion.div animate={{
                y: [0, 8, 0]
              }} transition={{
                duration: 5,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="bg-brand-navy text-white rounded-xl shadow-xl shadow-slate-900/20 px-3.5 py-2.5 flex items-center gap-2.5" data-id="element-2761">
                  <div className="bg-brand-coral/20 p-1.5 rounded-md" data-id="element-2762">
                    <TrendingUpIcon className="w-4 h-4 text-brand-coral" data-id="element-2763" />
                  </div>
                  <div data-id="element-2764">
                    <div className="text-[10px] font-semibold text-brand-coral uppercase tracking-wider" data-id="element-2765">
                      Win Rate
                    </div>
                    <div className="text-sm font-bold" data-id="element-2766">+34% YoY</div>
                  </div>
                </motion.div>
              </motion.div>

              <motion.div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl shadow-slate-300/40 border border-slate-100 overflow-hidden z-10 transition-shadow hover:shadow-slate-400/40" initial={{
              y: 40,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              duration: 0.8,
              ease: 'easeOut'
            }} whileHover={{
              y: -6
            }} data-id="element-2767">
                <div className="bg-brand-navy text-white p-5 relative overflow-hidden" data-id="element-2768">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-brand-teal/10 rounded-full blur-2xl" data-id="element-2769" />
                  <div className="relative" data-id="element-2770">
                    <div className="flex items-center gap-2 mb-1.5" data-id="element-2771">
                      <span className="bg-brand-coral/15 text-brand-coral text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-2772">
                        DMC View
                      </span>
                      <span className="flex items-center text-xs text-slate-300" data-id="element-2773">
                        <MapPinIcon className="w-3 h-3 mr-1" data-id="element-2774" /> Punta Cana
                      </span>
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-2775">
                      Corporate Leadership Team
                    </h3>
                    <p className="text-xs text-slate-400" data-id="element-2776">
                      Incentive Retreat • 5 nights
                    </p>
                  </div>
                </div>

                <div className="p-6 space-y-5 bg-slate-50/50" data-id="element-2777">
                  <div className="grid grid-cols-2 gap-4" data-id="element-2778">
                    <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm" data-id="element-2779">
                      <div className="flex justify-between items-center mb-2" data-id="element-2780">
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-2781">
                          Attendees
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-brand-navy" data-id="element-2782">
                        85
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm" data-id="element-2783">
                      <div className="flex justify-between items-center mb-2" data-id="element-2784">
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-2785">
                          Intake
                        </span>
                        <span className="text-sm font-bold text-brand-navy" data-id="element-2786">
                          91%
                        </span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden" data-id="element-2787">
                        <motion.div className="bg-brand-teal h-2 rounded-full" initial={{
                        width: 0
                      }} animate={{
                        width: '91%'
                      }} transition={{
                        duration: 1.2,
                        delay: 0.5
                      }} data-id="element-2788" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3" data-id="element-2789">
                    {[{
                    icon: UsersRoundIcon,
                    label: 'Group Matching',
                    status: 'Ready',
                    color: 'emerald'
                  }, {
                    icon: HeartIcon,
                    label: 'Activity Preferences',
                    status: 'Captured',
                    color: 'teal'
                  }, {
                    icon: BarChart3Icon,
                    label: 'Client Report',
                    status: 'Preview Available',
                    color: 'coral'
                  }].map((item, i) => {
                    const colorMap: Record<string, string> = {
                      emerald: 'text-emerald-600 bg-emerald-50',
                      teal: 'text-brand-teal bg-brand-teal/10',
                      coral: 'text-brand-coral bg-brand-coral/10'
                    };
                    return <div key={i} className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-100 shadow-sm" data-id="element-2790">
                          <div className="flex items-center gap-3" data-id="element-2791">
                            <div className="bg-slate-100 p-2 rounded-md" data-id="element-2792">
                              <item.icon className="w-4 h-4 text-brand-navy" data-id="element-2793" />
                            </div>
                            <span className="text-sm font-medium text-slate-700" data-id="element-2794">
                              {item.label}
                            </span>
                          </div>
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${colorMap[item.color]}`} data-id="element-2795">
                            {item.status}
                          </span>
                        </div>;
                  })}
                  </div>

                  <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm" data-id="element-2796">
                    <div className="flex items-center justify-between mb-2" data-id="element-2797">
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-2798">
                        Proposal Margin Add-On
                      </span>
                      <span className="text-xs font-bold text-emerald-600 flex items-center gap-1" data-id="element-2799">
                        <TrendingUpIcon className="w-3 h-3" data-id="element-2800" /> +18%
                      </span>
                    </div>
                    <div className="text-lg font-bold text-brand-navy" data-id="element-2801">
                      $24,800
                    </div>
                  </div>
                </div>
              </motion.div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Destination Strip */}
      <section className="bg-brand-navy py-10 border-y border-brand-navy/80 relative overflow-hidden" data-id="element-2802">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 text-center" data-id="element-2803">
          <div className="text-xs font-bold text-slate-400 uppercase tracking-[0.2em] mb-6" data-id="element-2804">
            Powering premium experiences in
          </div>
          <div className="flex flex-wrap justify-center gap-x-12 gap-y-6 opacity-70" data-id="element-2805">
            {['Punta Cana', 'Tulum', 'Lisbon', 'Tuscany', 'Bali', 'Cape Town'].map((dest, i) => <FadeIn key={dest} delay={i * 0.1} data-id="element-2806">
                <div className="font-serif text-xl md:text-2xl text-white font-medium" data-id="element-2807">
                  {dest}
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Problem */}
      <Section className="bg-slate-50" data-id="element-2808">
        <FadeIn className="text-center max-w-3xl mx-auto mb-14" data-id="element-2809">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2810">
            DMCs are often judged on logistics, even when clients want more.
          </h2>
        </FadeIn>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-2811">
          {['Proposals compete on similar itineraries', 'Clients want engagement, wellness, and culture', 'Event goals are often vague', 'Attendee preferences are collected too late', 'Post-event reporting is usually weak', 'It is hard to prove strategic value'].map((p, i) => <FadeIn key={i} delay={i * 0.05} data-id="element-2812">
              <div className="bg-white p-6 rounded-2xl border border-slate-100 h-full" data-id="element-2813">
                <div className="w-10 h-10 rounded-lg bg-brand-coral/10 text-brand-coral flex items-center justify-center mb-4" data-id="element-2814">
                  <XIcon className="w-5 h-5" data-id="element-2815" />
                </div>
                <p className="font-semibold text-brand-navy" data-id="element-2816">{p}</p>
              </div>
            </FadeIn>)}
        </div>
      </Section>

      {/* Before / After */}
      <Section className="bg-white" data-id="element-2817">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2818">
          <FadeIn data-id="element-2819">
            <div className="inline-flex items-center gap-2 bg-brand-teal/10 text-brand-teal text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider mb-4" data-id="element-2820">
              <TrophyIcon className="w-3.5 h-3.5" data-id="element-2821" /> Win More RFPs
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2822">
              Give your proposal a stronger reason to win.
            </h2>
          </FadeIn>
        </div>
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8" data-id="element-2823">
          <FadeIn delay={0.1} data-id="element-2824">
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200 h-full" data-id="element-2825">
              <div className="flex items-center gap-3 mb-6" data-id="element-2826">
                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-500" data-id="element-2827">
                  <XCircleIcon className="w-5 h-5" data-id="element-2828" />
                </div>
                <h3 className="text-xl font-bold text-slate-700" data-id="element-2829">
                  Standard DMC Proposal
                </h3>
              </div>
              <ul className="space-y-4" data-id="element-2830">
                {['Itinerary spreadsheet', 'Competing primarily on logistics and price', 'Manual dietary and activity collection', 'Generic team building activities', 'No post-event proof of ROI'].map((item, i) => <li key={i} className="flex items-start gap-3 text-slate-600" data-id="element-2831">
                    <XIcon className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" data-id="element-2832" />
                    <span data-id="element-2833">{item}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
          <FadeIn delay={0.2} data-id="element-2834">
            <div className="bg-brand-navy rounded-2xl p-8 border border-brand-navy shadow-xl h-full relative overflow-hidden" data-id="element-2835">
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-brand-coral/10 rounded-full blur-3xl" data-id="element-2836" />
              <div className="relative z-10" data-id="element-2837">
                <div className="flex items-center gap-3 mb-6" data-id="element-2838">
                  <div className="w-10 h-10 rounded-full bg-brand-coral/20 flex items-center justify-center text-brand-coral" data-id="element-2839">
                    <CheckCircle2Icon className="w-5 h-5" data-id="element-2840" />
                  </div>
                  <h3 className="text-xl font-bold text-white" data-id="element-2841">
                    With Offsite+
                  </h3>
                </div>
                <ul className="space-y-4" data-id="element-2842">
                  {['Structured experience layer', 'Premium margin add-on', 'Automated intake and group matching', 'Curated learning and wellness pathways', 'Executive-ready report for the client'].map((item, i) => <li key={i} className="flex items-start gap-3 text-slate-300" data-id="element-2843">
                      <CheckCircle2Icon className="w-5 h-5 text-brand-coral shrink-0 mt-0.5" data-id="element-2844" />
                      <span data-id="element-2845">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Solution */}
      <Section id="solution" className="bg-slate-50" data-id="element-2846">
        <FadeIn className="text-center max-w-3xl mx-auto mb-16" data-id="element-2847">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2848">
            One product layered on top of the logistics you already deliver.
          </h2>
        </FadeIn>

        <div className="grid md:grid-cols-3 gap-6 relative" data-id="element-2849">
          <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-slate-200 z-0" data-id="element-2850" />
          {[{
          icon: ClipboardCheckIcon,
          phase: 'Before',
          color: 'text-brand-teal',
          bg: 'bg-brand-teal/10',
          items: ['AI offsite blueprint', 'Attendee intake', 'Goals and preferences', 'Agenda strategy']
        }, {
          icon: SparklesIcon,
          phase: 'During',
          color: 'text-brand-coral',
          bg: 'bg-brand-coral/10',
          items: ['Event hub', 'Group matching', 'Learning prompts', 'Activity signups']
        }, {
          icon: BarChart3Icon,
          phase: 'After',
          color: 'text-brand-navy',
          bg: 'bg-brand-navy/10',
          items: ['Surveys', 'Commitments', 'Insights', 'Client-ready report']
        }].map((s, i) => <FadeIn key={i} delay={i * 0.15} className="relative z-10" data-id="element-2851">
              <div className="bg-white border border-slate-100 rounded-2xl p-8 h-full shadow-sm flex flex-col items-center text-center" data-id="element-2852">
                <div className={`${s.bg} ${s.color} w-14 h-14 rounded-2xl flex items-center justify-center mb-5`} data-id="element-2853">
                  <s.icon className="w-7 h-7" data-id="element-2854" />
                </div>
                <h3 className="text-xl font-bold text-brand-navy mb-5" data-id="element-2855">
                  {s.phase}
                </h3>
                <ul className="space-y-3 w-full text-left" data-id="element-2856">
                  {s.items.map((it, j) => <li key={j} className="flex items-center gap-2 text-sm text-slate-700 font-medium" data-id="element-2857">
                      <div className={`w-1.5 h-1.5 rounded-full ${s.color.replace('text-', 'bg-')}`} data-id="element-2858" />
                      {it}
                    </li>)}
                </ul>
              </div>
            </FadeIn>)}
        </div>
      </Section>

      {/* Proposal Upgrade Side-by-Side */}
      <Section id="proposal" className="bg-white" data-id="element-2859">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2860">
          <FadeIn data-id="element-2861">
            <div className="inline-block bg-brand-coral/10 text-brand-coral text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-5" data-id="element-2862">
              Proposal Upgrade
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2863">
              Add Offsite+ to your next client proposal.
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed" data-id="element-2864">
              A premium experience module that drops directly into your proposal
              — visually distinct, easily priced, and immediately
              differentiating.
            </p>
          </FadeIn>
        </div>

        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8 items-center" data-id="element-2865">
          <FadeIn delay={0.1} data-id="element-2866">
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 opacity-75" data-id="element-2867">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4" data-id="element-2868">
                Standard Proposal Line Items
              </div>
              <ul className="space-y-3" data-id="element-2869">
                {['Airport Transfers', 'Hotel Accommodations', 'Welcome Dinner', 'Meeting Room Rental', 'Catamaran Excursion'].map((it, i) => <li key={i} className="flex items-center gap-3 text-sm text-slate-600" data-id="element-2870">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-300" data-id="element-2871" />
                    {it}
                  </li>)}
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-2872">
            <div className="bg-white rounded-2xl shadow-xl border border-brand-coral/20 overflow-hidden relative" data-id="element-2873">
              <div className="absolute top-0 right-0 w-32 h-32 bg-brand-coral/10 rounded-full blur-2xl" data-id="element-2874" />
              <div className="bg-brand-navy text-white p-5 flex items-center justify-between relative z-10" data-id="element-2875">
                <div data-id="element-2876">
                  <div className="text-xs text-brand-coral uppercase tracking-wider font-bold mb-0.5" data-id="element-2877">
                    New Add-On Module
                  </div>
                  <h4 className="font-bold" data-id="element-2878">Offsite+ Experience Layer</h4>
                </div>
                <div className="text-right" data-id="element-2879">
                  <div className="text-xs text-slate-400" data-id="element-2880">Margin uplift</div>
                  <div className="text-lg font-bold text-brand-coral" data-id="element-2881">+18%</div>
                </div>
              </div>
              <div className="p-6 relative z-10" data-id="element-2882">
                <ul className="space-y-3" data-id="element-2883">
                  {['Attendee intake & preferences', 'AI-designed agenda', 'Digital event hub', 'Group matching algorithms', 'Post-event ROI report'].map((it, i) => <li key={i} className="flex items-center gap-3 text-sm text-slate-700 font-medium" data-id="element-2884">
                      <CheckCircle2Icon className="w-4 h-4 text-brand-teal shrink-0" data-id="element-2885" />
                      {it}
                    </li>)}
                </ul>
                <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between" data-id="element-2886">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-2887">
                    Suggested Add-On Price
                  </span>
                  <span className="text-lg font-bold text-brand-navy" data-id="element-2888">
                    $1,500–$3,500
                  </span>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* World-Class Content & Frameworks */}
      <Section className="bg-slate-50 border-y border-slate-100" data-id="element-2889">
        <div className="max-w-5xl mx-auto text-center" data-id="element-2890">
          <FadeIn data-id="element-2891">
            <h2 className="text-2xl md:text-3xl font-bold text-brand-navy mb-4" data-id="element-2892">
              Premium content your clients recognize.
            </h2>
            <p className="text-slate-600 mb-10 max-w-2xl mx-auto" data-id="element-2893">
              Every Offsite+ pathway is built from licensed and adapted material
              from top business schools and bestselling authors.
            </p>
          </FadeIn>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center opacity-70 grayscale hover:grayscale-0 transition-all duration-500" data-id="element-2894">
            {['Harvard Business School', 'Wharton Executive Education', 'INSEAD', 'Stanford GSB'].map((src, i) => <FadeIn key={src} delay={i * 0.1} data-id="element-2895">
                <div className="font-serif text-lg md:text-xl text-brand-navy font-medium leading-tight" data-id="element-2896">
                  {src}
                </div>
              </FadeIn>)}
          </div>
        </div>
      </Section>

      {/* DMC Value */}
      <Section id="value" className="bg-white" data-id="element-2897">
        <FadeIn className="text-center max-w-3xl mx-auto mb-16" data-id="element-2898">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2899">
            Why DMCs use Offsite+
          </h2>
        </FadeIn>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-2900">
          {[{
          icon: TrophyIcon,
          title: 'Win better proposals',
          desc: 'Show clients a smarter, more structured event plan.'
        }, {
          icon: DollarSignIcon,
          title: 'Increase margins',
          desc: 'Package Offsite+ as a premium experience add-on.'
        }, {
          icon: RotateCcwIcon,
          title: 'Reduce planning chaos',
          desc: 'Collect attendee details, preferences, and expectations in one flow.'
        }, {
          icon: SparklesIcon,
          title: 'Improve onsite engagement',
          desc: 'Use prompts, group formats, and event hubs to guide the attendee experience.'
        }, {
          icon: FileTextIcon,
          title: 'Deliver proof',
          desc: 'Give the client a professional post-event report they can share internally.'
        }, {
          icon: HandshakeIcon,
          title: 'Build long-term partnerships',
          desc: 'Turn one-off events into recurring, measurable client relationships.'
        }].map((c, i) => <FadeIn key={i} delay={i * 0.07} data-id="element-2901">
              <motion.div whileHover={{
            y: -4
          }} className="bg-white p-6 rounded-2xl border border-slate-100 h-full shadow-sm transition-shadow hover:shadow-md" data-id="element-2902">
                <div className="w-11 h-11 bg-brand-teal/10 text-brand-teal rounded-xl flex items-center justify-center mb-4" data-id="element-2903">
                  <c.icon className="w-5 h-5" data-id="element-2904" />
                </div>
                <h3 className="font-bold text-brand-navy mb-2" data-id="element-2905">{c.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-2906">
                  {c.desc}
                </p>
              </motion.div>
            </FadeIn>)}
        </div>
      </Section>

      {/* Reporting */}
      <Section id="reports" className="bg-slate-50" data-id="element-2907">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-2908">
          <FadeIn data-id="element-2909">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2910">
              Prove what happened after the event.
            </h2>
            <p className="text-lg text-slate-600 mb-6 leading-relaxed" data-id="element-2911">
              Hand the client a polished, shareable report — and turn one-off
              events into long-term partnerships.
            </p>
            <Button variant="primary" size="lg" href="/report" data-id="element-2912">
              See Sample Report
              <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-2913" />
            </Button>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-2914">
            <div className="bg-white rounded-2xl shadow-soft border border-slate-100 p-7" data-id="element-2915">
              <div className="flex items-center justify-between mb-6" data-id="element-2916">
                <h3 className="font-bold text-lg text-brand-navy" data-id="element-2917">
                  Post-Event Snapshot
                </h3>
                <span className="bg-brand-teal/10 text-brand-teal text-xs font-bold px-3 py-1 rounded-full" data-id="element-2918">
                  Final
                </span>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-5" data-id="element-2919">
                <div className="bg-slate-50 p-3 rounded-xl text-center" data-id="element-2920">
                  <div className="text-xl font-black text-brand-navy mb-0.5" data-id="element-2921">
                    89%
                  </div>
                  <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider" data-id="element-2922">
                    Survey
                  </div>
                </div>
                <div className="bg-slate-50 p-3 rounded-xl text-center" data-id="element-2923">
                  <div className="text-xl font-black text-brand-teal mb-0.5" data-id="element-2924">
                    76%
                  </div>
                  <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider" data-id="element-2925">
                    Group Activity
                  </div>
                </div>
                <div className="bg-slate-50 p-3 rounded-xl text-center" data-id="element-2926">
                  <div className="text-xl font-black text-brand-coral mb-0.5" data-id="element-2927">
                    8.8
                  </div>
                  <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider" data-id="element-2928">
                    Experience
                  </div>
                </div>
              </div>

              <div className="bg-brand-navy text-white p-4 rounded-xl mb-5 flex items-center justify-between" data-id="element-2929">
                <div className="flex items-center gap-3" data-id="element-2930">
                  <CheckCircle2Icon className="w-7 h-7 text-brand-teal" data-id="element-2931" />
                  <div data-id="element-2932">
                    <div className="text-xs text-slate-300" data-id="element-2933">
                      Commitments Captured
                    </div>
                    <div className="font-bold" data-id="element-2934">Across 85 attendees</div>
                  </div>
                </div>
                <div className="text-2xl font-bold" data-id="element-2935">52</div>
              </div>

              <div className="border border-slate-100 rounded-xl p-4" data-id="element-2936">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-2937">
                  Top Themes
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-2938">
                  {['Communication', 'Trust', 'Leadership', 'Energy'].map(t => <span key={t} className="bg-slate-100 text-slate-700 text-xs px-2.5 py-1 rounded-md font-medium" data-id="element-2939">
                        {t}
                      </span>)}
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Partner Plan */}
      <Section id="partner" className="bg-white" data-id="element-2940">
        <FadeIn className="text-center max-w-3xl mx-auto mb-14" data-id="element-2941">
          <div className="inline-block bg-brand-teal/10 text-brand-teal text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-4" data-id="element-2942">
            Partner Plan
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2943">
            Built for DMCs running multiple events.
          </h2>
        </FadeIn>

        <div className="grid lg:grid-cols-3 gap-8 items-start" data-id="element-2944">
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4" data-id="element-2945">
            {[{
            icon: PaletteIcon,
            label: 'White-label client hubs'
          }, {
            icon: LayersIcon,
            label: 'Reusable destination templates'
          }, {
            icon: UsersIcon,
            label: 'Team accounts'
          }, {
            icon: PackageIcon,
            label: 'Proposal assets'
          }, {
            icon: ClipboardCheckIcon,
            label: 'Branded intake forms'
          }, {
            icon: FileTextIcon,
            label: 'Client reports'
          }, {
            icon: GraduationCapIcon,
            label: 'Training for sales & ops teams'
          }, {
            icon: HeadphonesIcon,
            label: 'Priority setup'
          }].map((f, i) => <FadeIn key={i} delay={i * 0.04} data-id="element-2946">
                <div className="flex items-center gap-3 bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-2947">
                  <div className="w-10 h-10 rounded-lg bg-white text-brand-teal flex items-center justify-center shadow-sm shrink-0" data-id="element-2948">
                    <f.icon className="w-5 h-5" data-id="element-2949" />
                  </div>
                  <span className="text-sm font-semibold text-brand-navy" data-id="element-2950">
                    {f.label}
                  </span>
                </div>
              </FadeIn>)}
          </div>

          {/* Pricing card */}
          <FadeIn delay={0.2} data-id="element-2951">
            <div className="bg-brand-navy text-white rounded-2xl p-8 sticky top-28 shadow-xl relative overflow-hidden" data-id="element-2952">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-brand-teal via-brand-coral to-brand-teal" data-id="element-2953" />
              <h4 className="font-bold text-lg mb-6" data-id="element-2954">Pricing for DMCs</h4>

              <div className="space-y-4 mb-8" data-id="element-2955">
                <div className="border-b border-white/10 pb-3" data-id="element-2956">
                  <div className="text-xs text-slate-400 uppercase tracking-wider" data-id="element-2957">
                    Pilot Event
                  </div>
                  <div className="text-xl font-bold" data-id="element-2958">
                    From $1,500
                    <span className="text-sm font-medium text-slate-400" data-id="element-2959">
                      /event
                    </span>
                  </div>
                </div>
                <div className="border-b border-white/10 pb-3" data-id="element-2960">
                  <div className="text-xs text-slate-400 uppercase tracking-wider" data-id="element-2961">
                    Premium Event
                  </div>
                  <div className="text-xl font-bold" data-id="element-2962">
                    From $3,500
                    <span className="text-sm font-medium text-slate-400" data-id="element-2963">
                      /event
                    </span>
                  </div>
                </div>
                <div data-id="element-2964">
                  <div className="text-xs text-brand-coral uppercase tracking-wider font-bold" data-id="element-2965">
                    DMC Partner License
                  </div>
                  <div className="text-xl font-bold" data-id="element-2966">Custom annual pricing</div>
                </div>
              </div>

              <Button variant="accent" className="w-full" data-id="element-2967">
                Become a Partner
              </Button>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Testimonial */}
      <Section className="bg-slate-50" data-id="element-2968">
        <div className="max-w-4xl mx-auto text-center" data-id="element-2969">
          <FadeIn data-id="element-2970">
            <QuoteIcon className="w-12 h-12 text-brand-teal/20 mx-auto mb-6" data-id="element-2971" />
            <h2 className="text-2xl md:text-4xl font-bold text-brand-navy leading-tight mb-8" data-id="element-2972">
              "We used to lose RFPs to cheaper local operators. Now we include
              the Offsite+ experience layer in every proposal, and our win rate
              has jumped 34%."
            </h2>
            <div className="flex items-center justify-center gap-4" data-id="element-2973">
              <div className="w-12 h-12 rounded-full bg-slate-200 overflow-hidden" data-id="element-2974">
                <img src="https://i.pravatar.cc/150?img=68" alt="Avatar" className="w-full h-full object-cover" data-id="element-2975" />
              </div>
              <div className="text-left" data-id="element-2976">
                <div className="font-bold text-brand-navy" data-id="element-2977">Mateo Rodriguez</div>
                <div className="text-sm text-slate-500" data-id="element-2978">
                  Founder, Premier Caribbean DMC
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="bg-brand-navy text-white relative overflow-hidden" data-id="element-2979">
        <div className="absolute inset-0 pointer-events-none" data-id="element-2980">
          <div className="absolute -top-1/4 -right-1/4 w-1/2 h-1/2 bg-brand-teal/20 rounded-full blur-[120px]" data-id="element-2981" />
          <div className="absolute -bottom-1/4 -left-1/4 w-1/2 h-1/2 bg-brand-coral/20 rounded-full blur-[120px]" data-id="element-2982" />
        </div>

        <div className="relative z-10 text-center max-w-3xl mx-auto" data-id="element-2983">
          <FadeIn data-id="element-2984">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-balance text-white" data-id="element-2985">
              Give your next proposal a stronger reason to win.
            </h2>
            <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto" data-id="element-2986">
              Stand out in RFPs, raise your margins, and prove the value of
              every event you run.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-2987">
              <Button variant="accent" size="lg" data-id="element-2988">
                Book DMC Demo
              </Button>
              <Button variant="outline" size="lg" href="/designer" className="border-white/20 text-white hover:bg-white/10 hover:border-white/40" data-id="element-2989">
                Design a Free Offsite
              </Button>
            </div>
          </FadeIn>
        </div>
      </Section>

      <footer className="bg-white border-t border-slate-100 py-10" data-id="element-2990">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-2991">
          <Logo data-id="element-2992" />
          <div className="text-sm text-slate-400" data-id="element-2993">
            © {new Date().getFullYear()} Offsite+. All rights reserved.
          </div>
        </div>
      </footer>
    </div>;
}